import React from 'react';
import { resolveYogiAvayogi } from '../utils/yogi';
import { resolveInduLagna, PLANET_KALAS } from '../utils/induLagna';
import { ownershipFromLagna } from '../utils/ownership';
import { resolveArudhas, ARUDHA_NAMES, ARUDHA_MEANINGS } from '../utils/arudha';
import { favourablesFor } from '../utils/planetTraits';
import './PositionsTable.css';

const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

const NAKSHATRA_NAMES = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashirsa', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni',
  'Uttara Phalguni', 'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha',
  'Jyestha', 'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana',
  'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati',
];

const NAKSHATRA_LORDS = [
  'Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury',
];

const PLANET_ORDER = [
  'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu',
];

const NAKSHATRA_SPAN = 360 / 27;

/** Whole-sign house of a planet counted from a reference sign, which is house 1. */
function houseFrom(planetSign, referenceSign) {
  const p = SIGNS.indexOf(planetSign);
  const r = SIGNS.indexOf(referenceSign);
  if (p < 0 || r < 0) return null;
  return ((p - r + 12) % 12) + 1;
}

function nakshatraOf(longitude) {
  const lon = Number(longitude);
  if (!Number.isFinite(lon)) return null;
  const norm = ((lon % 360) + 360) % 360;
  const index = Math.floor(norm / NAKSHATRA_SPAN) % 27;
  const pada = Math.min(Math.floor((norm % NAKSHATRA_SPAN) / (NAKSHATRA_SPAN / 4)) + 1, 4);
  return { name: NAKSHATRA_NAMES[index], lord: NAKSHATRA_LORDS[index % 9], pada };
}

function ordinal(n) {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
  return `${n}${suffix}`;
}

/**
 * Planetary positions with the house counted from three reference points:
 * the Lagna, the Moon (Chandra Lagna), the Sun (Surya Lagna) and the
 * Indu Lagna (the wealth point).
 *
 * Computed here rather than read from the chart so the numbers are the same
 * ones the backend puts in the prompt, and so charts cached before a fix
 * still display correctly.
 */
function PositionsTable({ chartData, onClose, embedded = false }) {
  const lagnaSign = chartData?.houses?.ascendant?.sign;
  const moonSign = chartData?.planets?.Moon?.sign;
  const sunSign = chartData?.planets?.Sun?.sign;
  const planets = chartData?.planets || {};
  const yogi = resolveYogiAvayogi(chartData);
  const indu = resolveInduLagna(chartData);
  const arudhas = resolveArudhas(chartData);
  const favourable = yogi ? favourablesFor(yogi.birthYogi) : null;

  const rows = PLANET_ORDER.filter((name) => planets[name]).map((name) => {
    const p = planets[name];
    const nak = nakshatraOf(p.longitude);
    const states = [];
    if (p.retrograde) states.push('R');
    if (p.combust) states.push('C');
    return {
      name,
      position: p.formatted || `${p.sign} ${Number(p.degree || 0).toFixed(1)}°`,
      fromLagna: houseFrom(p.sign, lagnaSign),
      fromMoon: houseFrom(p.sign, moonSign),
      fromSun: houseFrom(p.sign, sunSign),
      fromIndu: indu ? houseFrom(p.sign, indu.sign) : null,
      fromArudha: arudhas?.A1 ? houseFrom(p.sign, arudhas.A1.sign) : null,
      ownership: ownershipFromLagna(name, lagnaSign),
      nakshatra: nak,
      states,
    };
  });

  const body = (
    <>
      <div className="positions-legend">
        {lagnaSign && (
          <span className="legend-item">
            <span className="legend-key">Lagna</span>
            {lagnaSign}
          </span>
        )}
        {moonSign && (
          <span className="legend-item">
            <span className="legend-key">Chandra Lagna</span>
            {moonSign}
          </span>
        )}
        {sunSign && (
          <span className="legend-item">
            <span className="legend-key">Surya Lagna</span>
            {sunSign}
          </span>
        )}
        {indu && (
          <span
            className="legend-item legend-indu"
            title={
              `9th lord from Lagna: ${indu.lordFromLagna} (${PLANET_KALAS[indu.lordFromLagna]} kalas). ` +
              `9th lord from Moon: ${indu.lordFromMoon} (${PLANET_KALAS[indu.lordFromMoon]} kalas). ` +
              `Total ${indu.totalKalas}, counted ${indu.remainder} signs from ${moonSign}.`
            }
          >
            <span className="legend-key">Indu Lagna</span>
            {indu.sign}
          </span>
        )}
        {arudhas?.A1 && (
          <span className="legend-item">
            <span className="legend-key">Arudha Lagna</span>
            {arudhas.A1.sign}
          </span>
        )}
        {yogi && (
          <span className="legend-item">
            <span className="legend-key">Yogi</span>
            {yogi.birthYogi}
          </span>
        )}
      </div>

      {favourable && (
        <div className="favourables">
          <div className="fav-row">
            <span className="fav-label">Lucky colours</span>
            <span className="fav-swatches" aria-hidden="true">
              {favourable.swatches.map((c) => (
                <span key={c} className="fav-dot" style={{ background: c }} />
              ))}
            </span>
            <span className="fav-value">{favourable.colours.join(', ')}</span>
          </div>
          <div className="fav-row">
            <span className="fav-label">Lucky number</span>
            <span className="fav-number">{favourable.number}</span>
            <span className="fav-value">{favourable.meaning}</span>
          </div>
          <p className="fav-note">
            From your Yogi planet, {favourable.planet} — the most supportive
            influence in your chart. These colours carry {favourable.colourMeaning}.
          </p>
        </div>
      )}

      <div className="positions-scroll">
        <table className="positions-table">
          <thead>
            <tr>
              <th>Planet</th>
              <th>Position</th>
              <th>Owns (from Lagna)</th>
              <th>Nakshatra</th>
              <th className="num">From Lagna</th>
              <th className="num">From Moon</th>
              <th className="num">From Sun</th>
              {indu && <th className="num">From Indu</th>}
              {arudhas?.A1 && <th className="num">From Arudha</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td className="planet-cell">
                  {r.name}
                  {r.states.length > 0 && (
                    <span className="planet-state" title={
                      r.states.includes('R') && r.states.includes('C')
                        ? 'Retrograde, Combust'
                        : r.states.includes('R') ? 'Retrograde' : 'Combust'
                    }>
                      {r.states.join('')}
                    </span>
                  )}
                </td>
                <td>{r.position}</td>
                <td className={`owns-cell${r.ownership?.isNode ? ' owns-node' : ''}`}
                    title={
                      r.ownership
                        ? (r.ownership.isNode
                            ? `${r.name} is a shadow point, associated with ${r.ownership.signs.join(', ')}`
                            : `${r.name} rules ${r.ownership.signs.join(' and ')}`)
                        : undefined
                    }>
                  {r.ownership ? r.ownership.label : '—'}
                </td>
                <td className="nak-cell">
                  {r.nakshatra
                    ? `${r.nakshatra.name} ${r.nakshatra.pada} · ${r.nakshatra.lord}`
                    : '—'}
                </td>
                <td className="num">{r.fromLagna ? ordinal(r.fromLagna) : '—'}</td>
                <td className="num">{r.fromMoon ? ordinal(r.fromMoon) : '—'}</td>
                <td className="num">{r.fromSun ? ordinal(r.fromSun) : '—'}</td>
                {indu && (
                  <td className="num">{r.fromIndu ? ordinal(r.fromIndu) : '—'}</td>
                )}
                {arudhas?.A1 && (
                  <td className="num">{r.fromArudha ? ordinal(r.fromArudha) : '—'}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {arudhas && (
        <div className="arudha-block">
          <h4 className="arudha-head">Arudha Padas</h4>
          <p className="arudha-sub">
            How each area of life is perceived from outside, as distinct from
            what it actually is.
          </p>
          <div className="positions-scroll">
            <table className="positions-table">
              <thead>
                <tr>
                  <th>Pada</th>
                  <th>Name</th>
                  <th>Sign</th>
                  <th className="num">From Lagna</th>
                  <th>Reads for</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(ARUDHA_NAMES)
                  .filter((k) => arudhas[k])
                  .map((k) => (
                    <tr key={k} className={k === 'A1' || k === 'A12' ? 'arudha-key' : undefined}>
                      <td className="planet-cell">{k}</td>
                      <td>{ARUDHA_NAMES[k]}</td>
                      <td>{arudhas[k].sign}</td>
                      <td className="num">{ordinal(arudhas[k].relativeHouse)}</td>
                      <td className="nak-cell">{ARUDHA_MEANINGS[k]}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="positions-note">
        Houses are whole-sign: each reference sign is its own 1st house —
        the Lagna sign from the Lagna, the Moon's sign from the Moon, the
        Sun's sign from the Sun, and the Indu Lagna sign from the Indu Lagna —
        the wealth point, used for questions about money and prosperity.
        Owns shows which houses from the Lagna each planet rules — two for most
        planets, one for the Sun and Moon. Rahu and Ketu are shadow points, so
        their associated sign is shown instead.
        {rows.some((r) => r.states.length > 0) && ' R = retrograde, C = combust.'}
      </p>
    </>
  );

  if (embedded) {
    return <div className="positions-embedded">{body}</div>;
  }

  return (
    <div className="positions-overlay" role="dialog" aria-modal="true" aria-label="Planetary positions">
      <div className="positions-backdrop" onClick={onClose} />
      <div className="positions-panel">
        <div className="positions-head">
          <h3>Planetary Positions</h3>
          <button type="button" className="positions-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {body}
      </div>
    </div>
  );
}

export default PositionsTable;
